<!--
 * @Description: 发票-开票数据模块（票税 v2）
 *               统一承载：类目分组(itemDetailList) → 蓝红票(blueRedInvoiceList)
 *               → 红冲金额输入框(8/9/10) → 其他材料(fileGroup)
 *
 *               buttonSource 直接接收父组件传入的 GtButtonSource，组件内不再做转换：
 *               1=上传蓝票（编辑全部蓝票卡）
 *               2=补充蓝票（已开票锁定卡 + 追加）
 *               3=修改蓝票（驳回提示 + 预填）
 *               4=上传红票（蓝票只读 + 红票上传 OCR）
 *               5=修改红票（蓝票只读 + 红票预填 + 驳回提示）
 *               6=审核蓝票 7=审核红票 11=发票详情（全部只读）
 *               8=申请冲红 9=重新申请冲红 10=继续冲红（蓝票只读 + 冲红金额可编辑）
 *               15=补充红票（部分冲红 RS=10 后，蓝票/已有红票只读 + 追加新红票）
 *
 *               锁定逻辑：nowAuditStatus === 2 时整卡锁定
 * @Date: 2026-06-05
-->
<template>
  <div class="invoice-blurred-upload">
    <!-- 顶部提示条：驳回 / 补传 -->
    <div v-if="isReject" class="invoice-blurred-upload__banner is-orange">
      审核驳回，请根据要求修改{{ isModifyRed ? '红票' : '蓝票' }}信息后重新提交
    </div>
    <div
      v-else-if="isSupplement"
      class="invoice-blurred-upload__banner is-orange"
    >
      请补传发票材料，原已开票记录不可修改
    </div>
    <div
      v-else-if="isSupplementRed"
      class="invoice-blurred-upload__banner is-orange"
    >
      请补传红票材料，蓝票及已有红票不可修改
    </div>

    <!-- ====== 1. 类目分组（itemDetailList，只读） ====== -->
    <template v-if="itemDetailList.length">
      <div class="invoice-blurred-upload__section-title">开票数据</div>
      <div
        v-for="(group, idx) in itemDetailList"
        :key="group.itemId"
        class="invoice-blurred-upload__category"
      >
        <div class="invoice-blurred-upload__category-header">
          开票类目 {{ idx + 1 }}：{{ group.itemName || '-' }}
        </div>
        <div class="invoice-blurred-upload__category-summary">
          共
          <b>1</b>
          条， 金额合计：
          <b style="color: var(--xrk-color-primary)">
            ¥{{ formatter.fen2yuan(group.invoiceSettlementSummaryAmount) }}
          </b>
        </div>
      </div>
      <!-- 蓝票计数 -->
      <div class="invoice-blurred-upload__count">
        已添加
        <b>{{ blueCount }}</b>
        张蓝票
      </div>
    </template>
    <!-- 红冲信息 -->
    <div
      v-if="info.redReason || info.maximumCreditableAmount"
      class="invoice-blurred-upload__section-title"
    >
      红冲信息
    </div>
    <div
      v-if="info.redReason || info.maximumCreditableAmount"
      class="invoice-blurred-upload__banner is-orange"
      style="
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-bottom: 12px;
      "
    >
      <div v-if="info.redReason">冲红原因：{{ info.redReason }}</div>
      <div v-if="info.redAmount">
        申请红冲金额：
        <b>¥{{ info.redAmount }}</b>
      </div>
    </div>

    <!-- ====== 2. 蓝红票（blueRedInvoiceList） ====== -->
    <!-- 蓝票卡片列表 -->
    <div class="invoice-blurred-upload__list">
      <BaseCard
        v-for="(item, index) in list"
        :key="item.blueInvoice?.id ?? index"
        class="invoice-blurred-upload__card"
      >
        <div class="invoice-blurred-upload__section-title">发票-蓝票</div>

        <!-- 锁定 badge -->
        <span
          v-if="isOriginLocked(item)"
          class="invoice-blurred-upload__badge is-blue"
        >
          已开票
        </span>
        <span
          v-else-if="isAuditLocked(item)"
          class="invoice-blurred-upload__badge is-green"
        >
          已通过
        </span>

        <!-- 蓝票照片 + 信息 -->
        <div class="invoice-blurred-upload__blue">
          <BaseImage
            v-if="item.blueInvoice?.invoicePic"
            class="invoice-blurred-upload__img"
            :src="item.blueInvoice.invoicePic"
            :preview-src-list="[item.blueInvoice.invoicePic]"
          />

          <div class="invoice-blurred-upload__fields">
            <!-- OCR 状态 -->
            <div
              v-if="item.blueInvoice?._ocrLoading"
              class="ocr-tip is-loading"
            >
              识别中...{{ item.blueInvoice?._ocrDone }}
            </div>
            <div v-else-if="item.blueInvoice?._ocrDone" class="ocr-tip is-done">
              ✓ OCR已识别，请核对，可手动修改
            </div>

            <div class="field">
              <span class="label">开票日期</span>
              <BaseInput
                v-model="item.blueInvoice.invoiceDate"
                placeholder="请输入"
                :disabled="!canEditBlue(item)"
              />
            </div>
            <div class="field">
              <span class="label">票面金额</span>
              <BaseInputNumber
                v-model="item.blueInvoice.invoiceAmount"
                placeholder="请输入"
                :controls="false"
                :precision="2"
                :min="0"
                :max="999999999"
                :disabled="!canEditBlue(item)"
              />
            </div>
            <div class="field">
              <span class="label">发票号码</span>
              <BaseInput
                v-model="item.blueInvoice.invoiceNo"
                placeholder="请输入"
                :disabled="!canEditBlue(item)"
              />
            </div>

            <!-- 删除已上传蓝票 -->
            <BaseIcon
              v-if="canEditBlue(item) && item.blueInvoice?.invoicePic"
              class="invoice-blurred-upload__remove"
              size="18"
              color="#ED7067"
              @click="onBlueRemove(index)"
            >
              <DeleteFilled />
            </BaseIcon>
          </div>
        </div>

        <!-- 红票区：上传红票 / 修改红票 / 详情有红票 -->
        <div v-if="showRedSection(item)" class="invoice-blurred-upload__red">
          <div class="invoice-blurred-upload__section-title">发票-红票</div>
          <!-- 红票卡片列表 -->
          <div
            v-if="(item.redInvoiceList || []).length"
            class="invoice-blurred-upload__red-list"
          >
            <div
              v-for="(red, rIndex) in item.redInvoiceList"
              :key="red.id ?? rIndex"
              class="invoice-blurred-upload__red-card"
              :class="{ 'is-locked': isRedLocked(red) }"
            >
              <span
                v-if="(isModifyRed || isSupplementRed) && !red._isNew"
                class="invoice-blurred-upload__badge is-orange"
              >
                {{ isSupplementRed ? '已冲红' : '原红票' }}
              </span>
              <span
                v-else-if="isRedNowAuditStatus2(red)"
                class="invoice-blurred-upload__badge is-green"
              >
                已通过
              </span>
              <BaseImage
                v-if="red.invoicePic"
                class="invoice-blurred-upload__img"
                :src="red.invoicePic"
                :preview-src-list="[red.invoicePic]"
              />
              <div class="invoice-blurred-upload__fields">
                <div v-if="red._ocrLoading" class="ocr-tip is-loading">
                  识别中...
                </div>
                <div v-else-if="red._ocrDone" class="ocr-tip is-done">
                  ✓ OCR已识别，请核对，可手动修改
                </div>
                <div class="field">
                  <span class="label">开票日期</span>
                  <BaseInput
                    v-model="red.invoiceDate"
                    placeholder="请输入开票日期"
                    :disabled="!canEditRed(item, red)"
                  />
                </div>
                <div class="field">
                  <span class="label">票面金额（负）</span>
                  <BaseInputNumber
                    v-model="red.invoiceAmount"
                    placeholder="请输入"
                    :controls="false"
                    :precision="2"
                    :min="0"
                    :max="999999999"
                    :disabled="!canEditRed(item, red)"
                  />
                </div>
                <div class="field">
                  <span class="label">发票号码</span>
                  <BaseInput
                    v-model="red.invoiceNo"
                    placeholder="请输入"
                    :disabled="!canEditRed(item, red)"
                  />
                </div>
                <BaseIcon
                  v-if="canEditRed(item, red)"
                  class="invoice-blurred-upload__remove"
                  size="18"
                  color="#ED7067"
                  @click="onRedRemove(item, rIndex)"
                >
                  <DeleteFilled />
                </BaseIcon>
              </div>
            </div>
          </div>

          <!-- 添加红票（uploadRed/modifyRed 且本卡未锁定时） -->
          <BaseUpload
            v-if="canAddRed"
            autoUpload
            btnText="+添加红票"
            tipText="只能上传jpg/png，且每份不超过50MB"
            :listType="null"
            :accept="['.jpg', '.jpeg', '.png', '.JPG', '.JPEG', '.PNG']"
            :limit="20"
            :limitSize="50"
            :multiple="false"
            :showFileList="false"
            :http-request="uploadApi"
            @change="v => onRedUpload(item, v)"
          ></BaseUpload>
        </div>
      </BaseCard>

      <!-- 添加蓝票（新增卡片） -->
      <div v-if="canAddBlue" class="invoice-blurred-upload__adder">
        <BaseUpload
          autoUpload
          btnText="+添加蓝票"
          tipText="只能上传jpg/png，且每份不超过50MB"
          :listType="null"
          :accept="['.jpg', '.jpeg', '.png', '.JPG', '.JPEG', '.PNG']"
          :limit="20"
          :limitSize="50"
          :multiple="false"
          :showFileList="false"
          :http-request="uploadApi"
          @change="onBlueUpload"
        ></BaseUpload>
      </div>
    </div>

    <!-- ====== 3. 红冲金额输入框（8/9/10） ====== -->
    <template v-if="isRedApplyMode">
      <div class="invoice-blurred-upload__section-title">申请冲红</div>
      <div class="invoice-blurred-upload__red-amount">
        <span class="label">冲红金额</span>
        <BaseInputNumber
          v-model="redAmount"
          style="width: 240px"
          placeholder="请输入"
          :controls="false"
          :precision="2"
          :step="0.01"
          :min="0"
          :max="maxRedAmount"
        />
        <span class="tip">
          申请红冲金额上限：
          <b>¥{{ maxRedAmount.toFixed(2) }}</b>
        </span>
      </div>
    </template>

    <!-- ====== 4. 其他材料：蓝 ====== -->
    <template v-if="canShowOtherBlue">
      <div class="invoice-blurred-upload__section-title">
        其他材料（蓝票）
        <span
          v-if="!canEditOtherBlue"
          class="invoice-blurred-upload__badge-inline is-grey"
        >
          仅查看
        </span>
      </div>
      <BaseUpload
        listType="text"
        autoUpload
        showFileList
        btnText="添加文件"
        :tipText="
          canEditOtherBlue
            ? '只能上传png/jpg/jpeg/pdf/bmp文件，且每份不超过50MB'
            : ''
        "
        :accept="['.jpg', '.jpeg', '.png', '.pdf', '.bmp']"
        :limit="canEditOtherBlue ? 10 : 0"
        :limitSize="50"
        :multiple="false"
        :http-request="uploadApi"
        :disabled="!canEditOtherBlue"
        :uploadBtnProps="{ limitOverHide: true }"
        v-model:fileList="blueOtherFiles"
        @preview="onFilePreview"
      />
    </template>

    <!-- ====== 4. 其他材料：红 ====== -->
    <template v-if="canShowOtherRed">
      <div class="invoice-blurred-upload__section-title">
        其他材料（红票）
        <span
          v-if="!canEditOtherRed"
          class="invoice-blurred-upload__badge-inline is-grey"
        >
          仅查看
        </span>
      </div>
      <BaseUpload
        listType="text"
        autoUpload
        showFileList
        btnText="添加文件"
        :tipText="
          canEditOtherRed
            ? '只能上传png/jpg/jpeg/pdf/bmp文件，且每份不超过50MB'
            : ''
        "
        :accept="['.jpg', '.jpeg', '.png', '.pdf', '.bmp']"
        :limit="canEditOtherRed ? 10 : 0"
        :limitSize="50"
        :multiple="false"
        :http-request="uploadApi"
        :disabled="!canEditOtherRed"
        :uploadBtnProps="{ limitOverHide: true }"
        v-model:fileList="redOtherFiles"
        @preview="onFilePreview"
      />
    </template>

    <!-- 底部汇总 -->
    <div
      class="invoice-blurred-upload__summary"
      v-if="isSupplement || isRedApplyMode"
    >
      <span v-if="isSupplement">
        原上传
        <b>{{ originBlueCount }}</b>
        张 + 本次新增
        <b style="color: #e6a23c">{{ newBlueCount }}</b>
        张
      </span>
      <span v-else-if="isRedApplyMode">
        本次冲红
        <b>¥{{ (redAmount || 0).toFixed(2) }}</b>
        / 原票面金额合计
        <b>¥{{ totalBlueAmountYuan }}</b>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from 'vue';
import BigNumber from 'bignumber.js';
import {
  BaseUpload,
  BaseImage,
  BaseInput,
  BaseInputNumber,
  BaseIcon,
  BaseCard,
  XrkMessage
} from 'xrk-components';
import { DeleteFilled } from '@element-plus/icons-vue';
import { uploadApi } from '@data-source/common/apis';
import {
  GtBlueRedInvoiceItem,
  GtInvoiceDetailData,
  GtFileGroup,
  InvoiceApplyDetailImgDTO
} from '@V/invoice/src/data-source/types';
import type { GtButtonSource } from '@V/invoice/src/types';
import { recognition } from '@V/invoice/src/data-source/apis';
import { formatter } from '@/utils';

const props = withDefaults(
  defineProps<{
    buttonSource?: GtButtonSource;
    /** 发票申请详情 id */
    id?: number;
  }>(),
  {
    buttonSource: 1,
    id: null
  }
);

// 双向绑定开票数据（蓝红票 / 类目 / 附件 / info），便于增删改回写父级
const formData = defineModel<GtInvoiceDetailData>('formData', {
  default: () => ({
    blueRedInvoiceList: [],
    fileGroup: { blueFileList: [], redFileList: [] },
    info: {} as GtInvoiceDetailData['info']
  })
});

// 申请冲红金额（元；8/9/10 时可编辑）
const redAmount = defineModel<number>('redAmount', { default: 0 });

/* ---------------- formData 子字段视图 ---------------- */
const list = computed(() => formData.value.blueRedInvoiceList || []);
const itemDetailList = computed(
  () => formData.value.info?.itemDetailList || []
);

/* ---------------- 其他材料：DTO ↔ BaseUpload 格式双向映射 ---------------- */
type FileDTO = NonNullable<GtFileGroup['blueFileList']>[number];

const toUpload = (dto: FileDTO) => ({
  fileUrl: dto.fileUrl,
  fileName: dto.fileName,
  name: dto.fileName,
  url: dto.fileUrl
});

const dtoListFrom = (
  uploads: any[],
  existingDTOs: FileDTO[],
  invoiceType: 1 | 2
): FileDTO[] =>
  uploads.map((u, i) => ({
    id: existingDTOs[i]?.id,
    invoiceApplyId: existingDTOs[i]?.invoiceApplyId || props.id,
    fileUrl: (u.fileUrl || u.url || '') as string,
    fileName: (u.fileName || u.name || '') as string,
    invoiceType,
    name: (u.fileName || u.name || '') as string,
    createAt: existingDTOs[i]?.createAt || Date.now(),
    remark: existingDTOs[i]?.remark,
    url: (u.fileUrl || u.url || '') as string,
    uid: existingDTOs[i]?.uid || Date.now(),
    status: existingDTOs[i]?.status
  }));

const blueOtherFiles = ref<any[]>([]);
const redOtherFiles = ref<any[]>([]);

watch(
  () => formData.value.fileGroup,
  fg => {
    if (fg?.blueFileList) {
      blueOtherFiles.value = fg.blueFileList.map(toUpload);
    }
    if (fg?.redFileList) {
      redOtherFiles.value = fg.redFileList.map(toUpload);
    }
  },
  { immediate: true }
);

watch(
  blueOtherFiles,
  v => {
    if (!formData.value.fileGroup)
      formData.value.fileGroup = { blueFileList: [], redFileList: [] };
    formData.value.fileGroup.blueFileList = dtoListFrom(
      v,
      formData.value.fileGroup?.blueFileList || [],
      1
    ) as any;
  },
  { deep: true }
);

watch(
  redOtherFiles,
  v => {
    if (!formData.value.fileGroup)
      formData.value.fileGroup = { blueFileList: [], redFileList: [] };
    formData.value.fileGroup.redFileList = dtoListFrom(
      v,
      formData.value.fileGroup?.redFileList || [],
      2
    );
  },
  { deep: true }
);
const info = computed(() => formData.value.info);

/* ---------------- 模式派生状态（直接基于 GtButtonSource） ---------------- */
/** 可编辑模式：1~5、补充红票 15；审核 6/7、冲红 8/9/10、详情 11 全部只读 */
const isEditMode = computed(() =>
  [1, 2, 3, 4, 5, 15].includes(props.buttonSource)
);
const isUploadBlue = computed(() => props.buttonSource === 1);
const isSupplement = computed(() => props.buttonSource === 2);
const isModifyBlue = computed(() => props.buttonSource === 3);
const isModifyRed = computed(() => props.buttonSource === 5);
/** 补充红票：部分冲红后追加新红票，蓝票/已有红票只读 */
const isSupplementRed = computed(() => props.buttonSource === 15);
const isReject = computed(() => isModifyBlue.value || isModifyRed.value);
/** 操作蓝票（上传/修改/补传） */
const isBlueOpMode = computed(
  () => isUploadBlue.value || isModifyBlue.value || isSupplement.value
);
/** 上传/修改红票 */
const isRedMode = computed(
  () => props.buttonSource === 4 || props.buttonSource === 5
);
/** 申请/重申/继续冲红 */
const isRedApplyMode = computed(() => [8, 9, 10].includes(props.buttonSource));

// 新增按钮：蓝票/红票操作模式本身即属可编辑模式，无需再判断 isEditMode
// 补充红票（15）只追加红票，蓝票区不可新增
const canAddBlue = computed(() => isBlueOpMode.value);
const canAddRed = computed(() => isRedMode.value || isSupplementRed.value);

/* ---------------- 锁定与可编辑判断 ---------------- */
/**
 * 锁定来源：blueInvoice.nowAuditStatus === 2 表示审核通过不可编辑
 */
const isAuditLocked = (item: GtBlueRedInvoiceItem) =>
  item.blueInvoice?.nowAuditStatus === 2;

/** 补传模式下原已开票卡片锁定（蓝色 badge） */
const isOriginLocked = (item: GtBlueRedInvoiceItem) =>
  isSupplement.value && !item._isNew;

const isCardLocked = (item: GtBlueRedInvoiceItem) =>
  isAuditLocked(item) || isOriginLocked(item);

/** 当前组中的红票审核通过 */
const isRedNowAuditStatus2 = (red: InvoiceApplyDetailImgDTO) =>
  red.nowAuditStatus === 2;
/** 红票自身是否锁定：自身 nowAuditStatus === 2，或补充红票模式下的原有红票 */
const isRedLocked = (red: InvoiceApplyDetailImgDTO) =>
  isRedNowAuditStatus2(red) || (isSupplementRed.value && !red._isNew);

/** 蓝票字段是否可编辑：先看是否可编辑模式，再看本卡 nowAuditStatus 锁定 */
const canEditBlue = (item: GtBlueRedInvoiceItem) => {
  if (!isEditMode.value || isCardLocked(item)) return false;
  // 补充红票（15）蓝票全部只读
  if (isSupplementRed.value) return false;
  if (isSupplement.value) return !!item._isNew;
  return isUploadBlue.value || isModifyBlue.value;
};

/** 红票字段是否可编辑：先看是否可编辑模式，再看父卡 / 红票自身 nowAuditStatus 锁定 */
const canEditRed = (
  item: GtBlueRedInvoiceItem,
  red: InvoiceApplyDetailImgDTO
) => {
  if (!isEditMode.value || isRedLocked(red)) return false;
  return isRedMode.value || isSupplementRed.value;
};

/** 是否展示某蓝票下的红票区 */
const showRedSection = (item: GtBlueRedInvoiceItem) => {
  if (isRedMode.value || isSupplementRed.value) {
    return true;
  }
  return (item.redInvoiceList || []).length > 0;
};

/* ---------------- 其他材料区可见/可编辑（操作谁谁可编辑） ---------------- */
const canEditOtherBlue = computed(() => isBlueOpMode.value);
const canEditOtherRed = computed(
  () => isRedMode.value || isSupplementRed.value
);

const canShowOtherBlue = computed(() => {
  if (canEditOtherBlue.value) return true;
  return blueOtherFiles.value.length > 0;
});
const canShowOtherRed = computed(() => {
  if (canEditOtherRed.value) return true;
  return redOtherFiles.value.length > 0;
});

/* ---------------- 计数与汇总 ---------------- */
const blueCount = computed(
  () => list.value.filter(i => i.blueInvoice?.invoicePic).length
);
const originBlueCount = computed(
  () => list.value.filter(i => i.blueInvoice?.invoicePic && !i._isNew).length
);
const newBlueCount = computed(() => list.value.filter(i => i._isNew).length);

/** 冲红金额上限（元）：info.maximumCreditableAmount 已由数据源层转元 */
const maxRedAmount = computed(
  () => Number(info.value.maximumCreditableAmount) || 0
);

/** 金额求和（元，bignumber.js，保留两位小数）；金额已由数据源层转元 */
const sumYuan = (nums: Array<number | null | undefined>) =>
  nums
    .reduce(
      (acc: BigNumber, n) => acc.plus(new BigNumber(n || 0)),
      new BigNumber(0)
    )
    .toFixed(2);

/** 蓝票票面金额合计（元，前端展示） */
const totalBlueAmountYuan = computed(() =>
  sumYuan(list.value.map(i => i.blueInvoice?.invoiceAmount))
);

/* ---------------- OCR ---------------- */
const runOcr = async (
  fileUrl: string
): Promise<Partial<InvoiceApplyDetailImgDTO>> => {
  try {
    const { data } = await recognition({ paramURL: fileUrl });
    // OCR amountInFiguers 为「元」字符串，bignumber.js 解析为数值（禁止 parseFloat）
    const ocrAmount = new BigNumber(data.amountInFiguers);
    return {
      invoiceDate: data.invoiceDate,
      invoiceAmount: ocrAmount.isNaN() ? 0 : ocrAmount.dp(2).toNumber(),
      invoiceNo: data.invoiceNumConfirm,
      purchaserName: data.purchaserName,
      purchaserRegisterNum: data.purchaserRegisterNum,
      sellerName: data.sellerName,
      sellerRegisterNum: data.sellerRegisterNum
    };
  } catch {
    return {};
  }
};

const createImg = (
  fileUrl: string,
  invoiceType: 1 | 2
): InvoiceApplyDetailImgDTO => ({
  invoiceApplyId: props.id,
  invoicePic: fileUrl,
  invoiceNo: '',
  invoiceAmount: null,
  invoiceDate: '',
  invoiceType,
  createAt: Date.now(),
  purchaserName: '',
  purchaserRegisterNum: '',
  sellerName: '',
  sellerRegisterNum: '',
  nowAuditStatus: 0,
  _isNew: true,
  _ocrLoading: true,
  _ocrDone: false
});

/* ---------------- 蓝票上传 / 删除 ---------------- */
// 校验上传结果结构、失败回滚、与父级提交逻辑对接
const onBlueUpload = async (v: any) => {
  const fileUrl = v?.response?.data?.fileUrl;
  if (!fileUrl) return;
  const item: GtBlueRedInvoiceItem = {
    blueInvoice: createImg(fileUrl, 1),
    redAmount: 0,
    redInvoiceList: [],
    _isNew: true
  };
  const ocr = await runOcr(fileUrl);
  Object.assign(item.blueInvoice, ocr, { _ocrLoading: false, _ocrDone: true });
  formData.value.blueRedInvoiceList.push(item);
};

// 删除时同步父级 / 后端删除标记
const onBlueRemove = (index: number) => {
  formData.value.blueRedInvoiceList.splice(index, 1);
};

/* ---------------- 红票上传 / 删除 ---------------- */
// 校验冲红金额不超过票面、红票金额聚合等业务规则
const onRedUpload = async (item: GtBlueRedInvoiceItem, v: any) => {
  const fileUrl = v?.response?.data?.fileUrl;
  if (!fileUrl) return;
  const red = createImg(fileUrl, 2);
  if (!item.redInvoiceList) item.redInvoiceList = [];
  const ocr = await runOcr(fileUrl);
  Object.assign(red, ocr, { _ocrLoading: false, _ocrDone: true });
  item.redInvoiceList.push(red);
};

// 删除时同步父级 / 后端删除标记
const onRedRemove = (item: GtBlueRedInvoiceItem, index: number) => {
  item.redInvoiceList.splice(index, 1);
};

/* ---------------- 文件预览 ---------------- */
const onFilePreview = (v: any) => {
  if (v?.url) window.open(v.url);
};

/* ---------------- 校验（暴露给父组件） ---------------- */
// 实际校验规则已与后端对齐，根据模式做最小化合法性校验
const validate = () => {
  const reject = (msg: string) => {
    XrkMessage.error(msg);
    return Promise.reject(new Error(msg));
  };
  if (isUploadBlue.value || isModifyBlue.value) {
    if (!list.value.length) return reject('请上传蓝票');
    for (const it of list.value) {
      if (isCardLocked(it)) continue;
      if (!it.blueInvoice?.invoicePic) {
        return reject('请上传蓝票照片');
      }
      if (!it.blueInvoice.invoiceAmount || !it.blueInvoice.invoiceNo) {
        return reject('请填写蓝票信息');
      }
    }
  }

  if (isSupplement.value) {
    const newOnes = list.value.filter(i => i._isNew);
    if (!newOnes.length) {
      return reject('请追加至少一张蓝票');
    }
    for (const it of newOnes) {
      if (!it.blueInvoice?.invoicePic) {
        return reject('请上传蓝票照片');
      }
      if (!it.blueInvoice.invoiceAmount || !it.blueInvoice.invoiceNo) {
        return reject('请填写蓝票信息');
      }
    }
  }

  if (isRedApplyMode.value) {
    if (!redAmount.value || redAmount.value <= 0) {
      return reject('请填写冲红金额');
    }
  }

  if (isRedMode.value || isSupplementRed.value) {
    const flat = list.value.flatMap(i => i.redInvoiceList || []);
    // 补充红票需追加新红票；上传/修改红票要求整体存在红票
    const pending = isSupplementRed.value ? flat.filter(r => r._isNew) : flat;
    if (!pending.length) {
      return reject(
        isSupplementRed.value ? '请追加至少一张红票' : '请上传红票'
      );
    }
    for (const r of pending) {
      if (isRedLocked(r)) continue;
      if (!r.invoicePic) {
        return reject('请上传红票照片');
      }
      if (!r.invoiceAmount || !r.invoiceNo) {
        return reject('请填写红票信息');
      }
    }
  }

  return Promise.resolve();
};

defineExpose({ validate });
</script>

<style lang="scss" scoped>
.invoice-blurred-upload {
  &__banner {
    margin-bottom: 12px;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 13px;

    &.is-orange {
      color: #e6a23c;
      background-color: #fdf6ec;
      border: 1px solid #f5dab1;
    }
  }

  &__section-title {
    margin-top: 16px;
    margin-bottom: 8px;
    padding-bottom: 6px;
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__category {
    background: #fafafa;
    padding: 10px 14px;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  &__category-header {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  &__category-summary {
    font-size: 13px;
    color: #666;
  }

  &__count {
    margin-bottom: 12px;
    font-size: 13px;
    color: #666;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__card {
    position: relative;

    &.is-locked {
      opacity: 0.7;
      pointer-events: none;
    }
  }

  &__badge {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    color: #fff;

    &.is-blue {
      background-color: #409eff;
    }

    &.is-green {
      background-color: #67c23a;
    }

    &.is-orange {
      background-color: #e6a23c;
    }
  }

  &__badge-inline {
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 11px;
    line-height: 18px;

    &.is-grey {
      background: #f0f2f5;
      color: #909399;
    }
  }

  &__blue,
  &__red-card {
    display: flex;
    gap: 16px;
  }

  &__red {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px dashed #f0d9a0;
  }

  &__red-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 12px 0;
  }

  &__red-card {
    position: relative;
    padding: 12px;
    border-radius: 6px;
    background-color: #fff;

    &.is-locked {
      opacity: 0.7;
      pointer-events: none;
    }
  }

  &__red-amount {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 4px;

    .label {
      font-size: 13px;
      color: #606266;
    }

    .tip {
      font-size: 12px;
      color: #909399;

      b {
        color: var(--xrk-color-primary);
      }
    }
  }

  &__img {
    width: 200px;
    height: 120px;
    flex-shrink: 0;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;

    .ocr-tip {
      font-size: 12px;

      &.is-loading {
        color: #909399;
      }

      &.is-done {
        color: #67c23a;
      }
    }

    .field {
      display: flex;
      flex-direction: column;

      .label {
        font-size: 12px;
        color: #999;
      }
    }
  }

  &__remove {
    position: absolute;
    right: -32px;
    bottom: 4px;
    cursor: pointer;
  }

  &__adder {
    padding: 16px;
    border: 1px dashed #dcdfe6;
    border-radius: 8px;
  }

  &__summary {
    margin-top: 16px;
    padding: 10px 14px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 4px;
    font-size: 14px;

    b {
      color: var(--xrk-color-primary);
    }
  }
}
</style>
